import { Dialog } from '@angular/cdk/dialog';
import { Injectable } from '@angular/core';
import { Auth, AuthConf, AuthLogoutReason, AuthSession } from '@navify-platform/auth';
import { Env } from '@navify-platform/env';
import { Idle, IdleEvent, IdleEventType, IdleOptions } from '@navify-platform/idle';
import { ConfigService } from 'src/app/shared/services/config.service';
import { ConfirmationPopupComponent } from '../components/confirmation-popup/confirmation-popup.component';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LabelService } from 'src/app/shared/services/label.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { OAuth2Service } from './oauth2.service';


@Injectable({
  providedIn: 'root'
})
export class NavifyService {
  arrYesNoCont: string[] = [];
  i18n = this.labelService.labels;
  sessionInfo!: AuthSession;
  authEnv!: Env;
  authConf!: AuthConf;
  auth!: Auth;
  idle!: Idle;
  userUuid!: string;
  readonly isIdle = new BehaviorSubject<boolean>(false);
  $isIdle = this.isIdle.asObservable();
  emailOfUserLoggedIn = new BehaviorSubject<string>('');
  emailID!: string;
  expiresAt: number = 0;
  intervalIdInfo: any;
  startTime = Date.now();
  userIdleTimeLimit = 1000 * 60 * 60 * 1; // 1 hour
  private idleInitialized = false;


  constructor(
    private readonly configServcie: ConfigService,
    private readonly dialog: Dialog,
    private readonly http: HttpClient,
    private readonly labelService: LabelService,
    private readonly oauth2Service: OAuth2Service
  ) {
    this.navifyAuthConfig();
  }

  navifyAuthConfig() {
    const env = new Env({
      props: {
        platformApiUrl: this.configServcie.getConfig()['platformApiUrl'],  // Navify Platform URL
        authUiUrl: this.configServcie.getConfig()['authUiUrl'],       // Authentication application URL
        appAlias: this.configServcie.getConfig()['appAlias'],        // Client application alias
        tenantAlias: this.configServcie.getConfig()['tenantAlias'],     // Client tenant alias
      },
    });

    this.authConf = {
      env,                                 // Env library instance
      sessionExpirationBeforeTime: 60,  // (1 min) How much time before expiration should the library emit the BEFORE_SESSION_EXPIRATION event (milliseconds)
      sessionPollingIntervalTime: 600,  // (10 mins) How often should the library send a polling request to check the existing session (milliseconds)
      sessionAutoRefresh: true,           // Flag deciding whether to refresh session automatically before session expiration
      redirectUrlUseHash: false,           // Flag deciding whether to use URL hash instead of query parameters
      identificationFormSkip: false,       // Flag deciding whether to force skip the identification step
      identificationFormSubmit: false,     // Flag deciding whether to submit the identification step if username is provided
      customPageTitle: 'Enclave',              // Page title customization HTML
      customBackgroundStyle: "url('${landingPage}') center no-repeat / cover",        // Background style customization CSS
      customHeader: 'Enclave',                 // Header customization HTML
      customHeadline: '',               // Headline customization HTML
      customCaption: '',                // Caption customization HTML
      customFooter: "<div class=''><a href='http://roche.com'>Go to Roche site</a></div>",                 // Footer customization HTML
      i18n: false,                         // Flag deciding whethet to internationalize the UI
      theme: 'navify2023'
    };

    const idleOptions: IdleOptions = {
      env: env,                             // environment, NavifyPlatformEnv instance
      syncTimeInterval: Number('1000') * Number('60') * Number('10'),     // clock sync interval time (optional)
      tickTimeInterval: Number('1000') * Number('60') * Number('1'),          // clock tick interval time (optional)
      idleTimeout: 1000 * 60 * 30,           // Idle timeout value override (optional)
      beforeIdleTimeSpan: 1000 * 60 * 1,        // how much time before idle the event is triggered (optional)
      toggleSyncActivity: true,             // flag deciding whether to synchronize activity information via API
      toggleTracking: true,                 // flag deciding whether to turn on DOM events tracking
    };

    this.auth = new Auth(this.authConf)
    this.idle = new Idle(idleOptions)
  }

  async navifyAuthInit() {
    await this.auth.init()
  }

  async getAuthSession() {
    return await this.auth.getSession();
  }

  async getLoginReturn() {
    return await this.auth.getLoginReturn();
  }

  async isLoggedIn() {
    await this.auth.init();
    if (this.idleInitialized === false) {
      await this.initiateIdle();
      this.idleInitialized = true;
    }
    await this.auth.getLoginReturn();
    const session: AuthSession = await this.auth.getSession();

    if (session === null) {
      return false
    }

    this.sessionInfo = session;
    this.emailID = this.sessionInfo.profile.username;
    this.emailOfUserLoggedIn.next(this.emailID);
    this.checkPermission();
    return true
  }

  getProfile() {
    const PlatformApiURL = this.configServcie.getConfig()['platformApiUrl']
    const profileURL = environment.apiProfile
    return new Promise((resolve, reject) => {
      this.http.get(PlatformApiURL + profileURL).subscribe({
        next: (data: any) => {
          this.userUuid = data.userUuid
          resolve(data)
        },
        error: (data) => {
          resolve(null)
        }
      })
    })
  }

  async checkPermission() {
    const tenantAlias = this.configServcie.getConfig()['tenantAlias'];
    const navifyData: any = await this.getProfile();
    if (navifyData) {
      this.triggerOAuth2FlowIfNeeded();
    }
    const aliasDetails = navifyData.tenants.filter((elm: any) => elm.name === tenantAlias);
    return aliasDetails.length !== 0;
  }

  private triggerOAuth2FlowIfNeeded(): void {
    const existingTokens = this.oauth2Service.getStoredTokens();
    const tokenSessionId = this.oauth2Service.getSessionIdFromToken();
    const cookieSessionId = this.oauth2Service.getCookieValue('session_id');
    const hasSessionIdMismatch = !!tokenSessionId && !!cookieSessionId && tokenSessionId !== cookieSessionId;

    if (hasSessionIdMismatch) {
      if (environment.oauth2Config.autoTriggerOnLogin === false) {
        console.info('[NavifyAuth] OAuth2 auto-trigger disabled â€” skipping session_id mismatch re-authorization');
      } else if (this.isOAuth2FlowInProgress()) {
        console.info('[NavifyAuth] OAuth2 flow already in progress â€” skipping session_id mismatch re-trigger');
      } else {
        this.startOAuth2Flow('[NavifyAuth] OAuth2 session_id mismatch detected â€” re-initiating authorization flow');
      }
    } else if (existingTokens?.id_token) {
      console.info('[NavifyAuth] Existing OAuth2 tokens found');
    } else if (
      environment.oauth2Config.autoTriggerOnLogin &&
      !this.oauth2Service.isExchangeCompleted() &&
      !this.oauth2Service.hasExchangeFailed()
    ) {
      if (this.isOAuth2FlowInProgress()) {
        console.info('[NavifyAuth] OAuth2 flow already in progress â€” skipping re-trigger');
      } else {
        this.startOAuth2Flow('[NavifyAuth] No OAuth2 tokens found â€” initiating authorization flow');
      }
    } else {
      const statusMessage = this.oauth2Service.hasExchangeFailed()
        ? '[NavifyAuth] Previous OAuth2 exchange failed â€” skipping auto re-trigger'
        : '[NavifyAuth] OAuth2 exchange already completed â€” skipping auto re-trigger';
      console.info(statusMessage);
    }
  }

  private isOAuth2FlowInProgress(): boolean {
    return sessionStorage.getItem('oauth2_flow_in_progress') === 'true';
  }

  private startOAuth2Flow(message: string): void {
    console.info(message);
    sessionStorage.setItem('oauth2_return_url', globalThis.location.pathname || '/m/applobby');
    sessionStorage.setItem('oauth2_flow_in_progress', 'true');
    sessionStorage.setItem('oauth2_auto_triggered', 'true');
    setTimeout(() => this.oauth2Service.initiateAuthorizationFlow(), 500);
  }

  resetOAuth2FlowFlag(): void {
    sessionStorage.removeItem('oauth2_flow_in_progress');
  }

  async loginToNavify(redirectSubRoute: string) {
    await this.auth.init();
    await this.auth.loginRedirect({
      returnTo: globalThis.location.href, // Login return URL
      username: '',
      state: {
        url: redirectSubRoute
      }
    });
  }

  async logOut(reason: AuthLogoutReason = AuthLogoutReason.userAction): Promise<void> {
    try {
      await firstValueFrom(this.oauth2Service.notifyBackendLogout());
    } catch {
    }

    this.oauth2Service.clearTokens();

    await this.auth.init();
    await this.auth.logoutRedirect({
      returnTo: globalThis.location.origin,
      state: {},
      reason: reason,
      logMessage: reason,
    });
    await this.auth.getLogoutReturn();
    await this.auth.destroy();
    await this.idle.destroy();
    sessionStorage.clear();
    localStorage.clear();
  }

 async initiateIdle(){
    await this.idle.init();
    this.idle.subscribe(async (event: IdleEvent) => {
      this.idleEventHandler(event)
    });
  }

  async idleEventHandler(event: IdleEvent) {
    if (IdleEventType.BeforeIdle === event.type) {
      const nowTime = Date.now();
      if (nowTime - this.startTime < this.userIdleTimeLimit) {
        await this.idle.markActivity();
      } else {
        const logoutTimer = setTimeout(async () => {
      dialogRef.close(); // Optionally close the dialog
      await this.logOut();
    }, 60000);
        const dialogRef = this.dialog.open(ConfirmationPopupComponent,
          {
            data: this.i18n.IDLE_POPUP
          }
        )
        dialogRef.closed.subscribe(async (res)=>{
          if(res){
            this.auth.init()
            this.idle.init()
             clearTimeout(logoutTimer); // Cancel auto-logout if user responds     
            this.idle.markActivity()
          } else{
            await this.logOut()
          }
        })
      }
    } else if (IdleEventType.Idle === event.type) {
      await this.logOut()
    }
  }

  async confirmIdleAction(idleOut: string) {
    this.arrYesNoCont.push(idleOut);
    await this.auth.init();
    await this.idle.init();
    if (idleOut === 'idle_yes' && this.arrYesNoCont.length === 1) {
      await this.idle.markActivity();
    }
    else if (idleOut === 'idle_no' && this.arrYesNoCont.length === 1) {
      await this.logOut();
    }
  }
}

